import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Divider, 
  Button, 
  Paper, 
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileImageUpload from '../components/ProfileImageUpload';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { RemoteTrack } from 'livekit-client';
import {
  startTrainingSession,
  stopTrainingSession,
  attachAudioTrack,
  checkBrowserSupport,
  TrainingSession,
} from '../services/livekit';
import { uploadAudioToFirebase, generateCallId, testFirebaseStorageConnection, testFirebaseStorageConnectionSimple } from '../services/firebaseStorage';
import { Scenario, DEFAULT_SCENARIOS } from '../services/scenarios';

// Styled components
const AppContainer = styled(Box)({
  display: 'flex',
  height: '100%',
  backgroundColor: '#f5f5f5'
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: '150px',
  backgroundColor: 'white',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),
}));

const MenuItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MenuItemText = styled(Typography)({
  fontSize: '0.85rem',
  color: '#666',
});

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  backgroundColor: '#f5f5f5',
  overflow: 'auto',
  height: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  color: '#334155',
  padding: theme.spacing(1, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 5),
  maxWidth: '1400px',
  margin: '0 auto',
}));

const TrainingCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
}));

const StartButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(2, 6),
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
  },
}));

const StopButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(2, 6),
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  backgroundColor: theme.palette.error.main,
  color: 'white',
  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  padding: theme.spacing(2, 1),
  fontSize: '0.95rem',
  fontWeight: 600,
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  backgroundColor: 'rgba(25, 118, 210, 0.05)',
  border: '1px solid rgba(25, 118, 210, 0.1)',
  marginTop: theme.spacing(3),
}));

const ScenarioSelector = styled(FormControl)(({ theme }) => ({
  minWidth: '400px',
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'white',
  },
}));

const Training: React.FC = () => {
  const { currentUser, userName } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State
  const [session, setSession] = useState<TrainingSession | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [agentConnected, setAgentConnected] = useState(false);
  const [browserSupported, setBrowserSupported] = useState(true);
  const sessionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  
  // Scenario state - brug hardcoded scenarier
  const allScenarios = DEFAULT_SCENARIOS;
  // Filtreret liste til visning - kun vis aktive scenarier
  const visibleScenarios = allScenarios.filter(s => s.id === 'nrgi_price_increase_2026' || s.id === 'mundtlig_eksamen');
  const [selectedScenario, setSelectedScenario] = useState<string>('mundtlig_eksamen');

  // Tjek browser support ved mount
  useEffect(() => {
    const support = checkBrowserSupport();
    setBrowserSupported(support.supported);
    if (!support.supported) {
      setError(`Din browser understøtter ikke: ${support.missingFeatures.join(', ')}`);
    }
    
    // Log tilgængelige scenarier
    console.log('📋 Tilgængelige scenarier:', visibleScenarios.map(s => s.name).join(', '));
  }, []);

  // Session duration timer
  useEffect(() => {
    if (session?.isConnected) {
      sessionTimerRef.current = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
    }

    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, [session?.isConnected]);

  // Format duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNavigate = (path: string) => {
    console.log(`Navigerer til: ${path}`); // Debugging log
    if (path === 'Race') {
      navigate('/race');
    } else if (path === 'Calls') {
      navigate('/calls');
    } else if (path === 'Dashboard') {
      navigate('/agent-dashboard');
    } else if (path === 'Stats') {
      navigate('/stats');
    } else if (path === 'Training') {
      navigate('/training');
    } else {
      // Disse sider er endnu ikke implementeret
      console.log(`Side '${path}' er ikke implementeret endnu`);
    }
  };

  const handleStartTraining = async () => {
    if (!currentUser || !userName) {
      setError('Du skal være logget ind for at starte træning');
      return;
    }

    try {
      setIsStarting(true);
      setError(null);
      setSessionDuration(0);
      setAgentConnected(false);

      const newSession = await startTrainingSession(
        currentUser.uid,
        userName,
        selectedScenario,
        (track: RemoteTrack) => {
          // Når agent sender audio
          console.log('Agent audio track modtaget');
          setAgentConnected(true);
          
          // Attach audio track til audio element
          const audioEl = attachAudioTrack(track, 'agent-audio');
          audioElementRef.current = audioEl;
        },
        (err: Error) => {
          setError(err.message);
        }
      );

      setSession(newSession);
      console.log('Træningssession startet:', newSession.roomName);
    } catch (err) {
      console.error('Fejl ved start af træning:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke starte træningssession');
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopTraining = async () => {
    if (!session) return;

    try {
      setIsStopping(true);
      setError(null);

      const recordings = await stopTrainingSession(session);
      
      console.log('📦 === OPTAGELSE MODTAGET ===');
      
      if (recordings.userWav && currentUser) {
        console.log('🎙️ Bruger WAV optagelse (høj kvalitet):', recordings.userWav.size, 'bytes');
        
        // Generer call ID
        const callId = generateCallId();
        console.log('📞 Call ID genereret:', callId);
        
        // Test Firebase Storage forbindelse først (simple test)
        console.log('🧪 Tester Firebase Storage forbindelse...');
        const simpleTest = await testFirebaseStorageConnectionSimple();
        
        if (!simpleTest) {
          console.error('❌ Firebase Storage simple test fejlede');
          setError('Kunne ikke forbinde til cloud storage. Tjek din internetforbindelse.');
          return;
        }
        
        console.log('✅ Firebase Storage simple test OK');
        
        // Test med upload
        console.log('🧪 Tester Firebase Storage upload...');
        const uploadTest = await testFirebaseStorageConnection();
        
        if (!uploadTest) {
          console.error('❌ Firebase Storage upload test fejlede');
          setError('Kunne ikke uploade til cloud storage. Tjek Firebase Storage regler.');
          return;
        }
        
        console.log('✅ Firebase Storage upload test OK');
        
        // Upload til Firebase Storage
        try {
          const downloadURL = await uploadAudioToFirebase(
            recordings.userWav,
            currentUser.uid,
            callId
          );
          
          console.log('✅ WAV fil uploaded til Firebase Storage');
          console.log('🔗 Download URL:', downloadURL);
          console.log('📁 Storage path: soundfiles/' + currentUser.uid + '/' + callId + '.wav');
          
          // Her kan du gemme metadata i Firestore hvis ønsket
          // await saveTrainingSession(currentUser.uid, callId, downloadURL, sessionDuration);
          
        } catch (uploadError) {
          console.error('❌ Fejl ved upload til Firebase:', uploadError);
          const errorMessage = uploadError instanceof Error ? uploadError.message : 'Ukendt fejl';
          setError('Kunne ikke uploade optagelse til cloud storage: ' + errorMessage);
          return;
        }
      }

      // Cleanup audio element
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current = null;
      }

      setSession(null);
      setAgentConnected(false);
      setSessionDuration(0);
      console.log('✅ Træningssession stoppet - optagelse uploaded til Firebase');
    } catch (err) {
      console.error('Fejl ved stop af træning:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke stoppe træningssession');
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <AppContainer>
      {/* Sidebar / Menu */}
      <Sidebar>
        <Box sx={{ padding: '10px', marginBottom: 2 }}>
          <ProfileImageUpload />
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Dashboard')} sx={{ marginTop: 1 }}>
          <MenuItemText>{t('common.dashboard')}</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Development</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Stats')}>
          <MenuItemText>Stats</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Calls')}>
          <MenuItemText>Calls</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Team</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Race')}>
          <MenuItemText>Race</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Training')} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
          <MenuItemText>{t('common.training')}</MenuItemText>
        </MenuItemContainer>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItemContainer onClick={() => handleNavigate('Help')}>
          <MenuItemText>Help</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Contact')}>
          <MenuItemText>Contact</MenuItemText>
        </MenuItemContainer>
      </Sidebar>
      
      {/* Main Content */}
      <MainContent>
        {/* Header */}
        <Header>
          <Typography variant="h6">{userName || "Voice Victor"}</Typography>
          <Typography variant="subtitle1">{t('common.training')}</Typography>
        </Header>
        
        {/* Content */}
        <ContentBox>
          <TrainingCard elevation={3}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              {/* Header */}
              <Box textAlign="center">
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('training.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('training.description')}
                </Typography>
              </Box>

              {/* Error Message */}
              {error && (
                <Alert 
                  severity="error" 
                  onClose={() => setError(null)}
                  sx={{ width: '100%', borderRadius: '8px' }}
                  icon={<ErrorIcon />}
                >
                  {error}
                </Alert>
              )}

              {/* Browser Support Warning */}
              {!browserSupported && (
                <Alert severity="warning" sx={{ width: '100%', borderRadius: '8px' }}>
                  {t('training.browserNotSupported')}
                </Alert>
              )}

              {/* Scenario Selector */}
              {!session && (
                <Box sx={{ width: '100%', maxWidth: '500px' }}>
                  <ScenarioSelector fullWidth>
                    <InputLabel id="scenario-select-label">
                      {t('training.selectScenario')}
                    </InputLabel>
                    <Select
                      labelId="scenario-select-label"
                      id="scenario-select"
                      value={selectedScenario}
                      label={t('training.selectScenario')}
                      onChange={(e) => setSelectedScenario(e.target.value)}
                      disabled={session !== null}
                    >
                      {visibleScenarios.map((scenario) => (
                        <MenuItem key={scenario.id} value={scenario.id}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {scenario.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {scenario.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </ScenarioSelector>
                  
                  {/* Vis valgt scenario beskrivelse */}
                  {allScenarios.find(s => s.id === selectedScenario) && (
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'rgba(25, 118, 210, 0.05)', 
                      borderRadius: '8px',
                      mb: 2 
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Scenario:</strong> {allScenarios.find(s => s.id === selectedScenario)?.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Status Display */}
              {session && (
                <>
                  <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                    <StatusChip
                      icon={<CheckCircleIcon />}
                      label={t('training.connected')}
                      color="success"
                      variant="filled"
                    />
                    {agentConnected && (
                      <StatusChip
                        icon={<VolumeUpIcon />}
                        label={t('training.agentConnected')}
                        color="primary"
                        variant="filled"
                      />
                    )}
                    <StatusChip
                      label={formatDuration(sessionDuration)}
                      variant="outlined"
                    />
                  </Box>
                  
                  {/* Vis valgt scenario under sessionen */}
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                    borderRadius: '12px',
                    border: '2px solid rgba(25, 118, 210, 0.2)',
                    maxWidth: '500px'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
                      Aktivt Scenario:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {allScenarios.find(s => s.id === selectedScenario)?.name || selectedScenario}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {allScenarios.find(s => s.id === selectedScenario)?.description}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Progress Bar when starting */}
              {isStarting && (
                <Box sx={{ width: '100%' }}>
                  <LinearProgress />
                  <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                    {t('training.connecting')}
                  </Typography>
                </Box>
              )}

              {/* Control Buttons */}
              <Box display="flex" gap={2}>
                {!session ? (
                  <StartButton
                    variant="contained"
                    size="large"
                    startIcon={isStarting ? <CircularProgress size={20} color="inherit" /> : <MicIcon />}
                    onClick={handleStartTraining}
                    disabled={isStarting || !browserSupported}
                  >
                    {isStarting ? t('training.starting') : t('training.startTraining')}
                  </StartButton>
                ) : (
                  <StopButton
                    variant="contained"
                    size="large"
                    startIcon={isStopping ? <CircularProgress size={20} color="inherit" /> : <StopIcon />}
                    onClick={handleStopTraining}
                    disabled={isStopping}
                  >
                    {isStopping ? t('training.stopping') : t('training.stopTraining')}
                  </StopButton>
                )}
              </Box>

              {/* Info Box */}
              <InfoBox>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {t('training.howItWorks')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                  <li>
                    <Typography variant="body2">
                      {t('training.step1')}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      {t('training.step2')}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      {t('training.step3')}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      {t('training.step4')}
                    </Typography>
                  </li>
                </Box>
              </InfoBox>

              {/* Hidden audio element for agent audio */}
              <audio id="agent-audio" style={{ display: 'none' }} />
            </Box>
          </TrainingCard>
        </ContentBox>
      </MainContent>
    </AppContainer>
  );
};

export default Training;

